import { LoadingButton } from '@mui/lab';
import { Box, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { isDesktop, hasCamera } from 'vig-ui-library';
import { useMagicLinkAppProvider, MagicLinkView } from '@/Provider/MagicLinkAppProvider';

export type PrimaryActionButtonProps = {
    startIcon?: ReactNode;
    fullWidth?: boolean;
};

const PrimaryActionButton = ({
    fullWidth = true,
}: PrimaryActionButtonProps) => {
    const { magicLinkData, confirmLinkTask, navigateToView, confirmError, frontSideUploadResult, backSideUploadResult } = useMagicLinkAppProvider();
    // ID card is NOT required if:
    // 1. Both sides are uploaded according to magicLinkData, OR
    // 2. Both frontSideUploadResult and backSideUploadResult are valid (not null)
    const bothSidesUploadedInData = magicLinkData?.idCardFrontUploaded && magicLinkData?.idCardBackUploaded;
    const bothSidesUploadedInResults = frontSideUploadResult !== null && backSideUploadResult !== null;
    const isIdCardRequired = !(bothSidesUploadedInData || bothSidesUploadedInResults);
    const desktop = isDesktop();
    const mobileWithCamera = !desktop && hasCamera();

    const handleClick = () => {
        if (!isIdCardRequired) {
            // When confirming, just perform the task and stay on the page
            confirmLinkTask.perform();
            return;
        }

        // Only navigate when ID card is required
        if (desktop || !mobileWithCamera) {
            // Desktop or mobile without camera: navigate to upload view
            navigateToView(MagicLinkView.UPLOAD_ID_CARD);
        } else if (mobileWithCamera) {
            // Mobile with camera: navigate to camera view
            navigateToView(MagicLinkView.CAMERA);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: fullWidth ? '100%' : 'auto' }}>
            {confirmError && !isIdCardRequired && (
                <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                    {confirmError}
                </Typography>
            )}
            <LoadingButton
                variant="contained"
                fullWidth={fullWidth}
                onClick={handleClick}
                loading={confirmLinkTask.isRunning}
                sx={{
                    textTransform: 'none',
                    height: '55px',
                    fontWeight: 500,
                }}
            >
                {isIdCardRequired ? 'Nahrať občiansky preukaz a potvrdiť žiadosť' : 'Potvrdiť žiadosť'}
            </LoadingButton>
        </Box>
    );
};

export default PrimaryActionButton;

